// src/utils/examClipboard.js
// Pasting into the exam (ExamWindowApp.jsx) only from inside the exam:
// students can copy and paste between their answers, the scratch pad and
// the exhibits, but text copied from anywhere else - another program, a
// website, an AI chat - is refused, so an answer can't simply be pasted in.
// Copying out of the exam still works, so students can keep their own copy.
//
// Every copy or cut made inside the exam window is remembered (its text,
// whitespace evened out, since the same text can come back from the
// clipboard with different line breaks); a paste or drop is let through
// only when its text is one of them. The toolbar Paste buttons read the
// clipboard themselves rather than through a paste event, so they check
// isExamCopy too (ExamRichTextEditor.jsx, ExamSpreadsheetGrid.jsx), and the
// toolbar Copy/Cut buttons call rememberExamCopy.

const MAX_REMEMBERED = 100;
const remembered = [];
// Only while the exam window has the guard on - the same editor and grid
// are used elsewhere (e.g. setting up a question's starting content), where
// pasting is unrestricted
let guardOn = false;

const normalize = (text) => String(text || '').replace(/\s+/g, ' ').trim();

export function rememberExamCopy(text) {
  const key = normalize(text);
  if (!key) return;
  const at = remembered.indexOf(key);
  if (at >= 0) remembered.splice(at, 1);
  remembered.push(key);
  if (remembered.length > MAX_REMEMBERED) remembered.shift();
}

export function isExamCopy(text) {
  if (!guardOn) return true;
  const key = normalize(text);
  return !key || remembered.includes(key);
}

export const PASTE_BLOCKED_MESSAGE = 'Pasting from outside the exam is not allowed. You can copy and paste within the exam.';

// The text a default (not script-handled) copy or drag takes
function selectedText() {
  const el = document.activeElement;
  if (el && (el.tagName === 'TEXTAREA' || (el.tagName === 'INPUT' && /^(text|search|)$/i.test(el.type || '')))) {
    return el.value.substring(el.selectionStart || 0, el.selectionEnd || 0);
  }
  return window.getSelection ? String(window.getSelection()) : '';
}

/**
 * Starts the guard on this window. onBlocked() is called whenever a paste or
 * drop is refused. Returns a function that stops it.
 */
export function installExamPasteGuard(onBlocked) {
  // After the editor's or grid's own handler has set what's copied (bubble
  // phase), so its exact text is what gets remembered
  const onCopy = (e) => {
    rememberExamCopy(e.clipboardData?.getData('text/plain') || selectedText());
  };
  const onDragStart = (e) => {
    rememberExamCopy(e.dataTransfer?.getData('text/plain') || selectedText());
  };
  // Before anything else sees it (capture phase)
  const refuse = (e) => {
    e.preventDefault();
    e.stopImmediatePropagation();
    onBlocked();
  };
  const onPaste = (e) => {
    const data = e.clipboardData;
    if (!data) return;
    const text = data.getData('text/plain');
    if (!text && (data.files?.length || data.getData('text/html'))) { refuse(e); return; }
    if (!isExamCopy(text)) refuse(e);
  };
  const onDrop = (e) => {
    const data = e.dataTransfer;
    if (!data) return;
    if (data.files?.length) { refuse(e); return; }
    if (!isExamCopy(data.getData('text/plain'))) refuse(e);
  };

  guardOn = true;
  window.addEventListener('copy', onCopy);
  window.addEventListener('cut', onCopy);
  window.addEventListener('dragstart', onDragStart);
  window.addEventListener('paste', onPaste, true);
  window.addEventListener('drop', onDrop, true);
  return () => {
    guardOn = false;
    window.removeEventListener('copy', onCopy);
    window.removeEventListener('cut', onCopy);
    window.removeEventListener('dragstart', onDragStart);
    window.removeEventListener('paste', onPaste, true);
    window.removeEventListener('drop', onDrop, true);
  };
}
